import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { SdmGlobal } from '../config/config';
import { PermissionService } from './permission.service';

// Use Auth0Lock from global scope (loaded via CDN)
declare const Auth0Lock: any;

@Injectable({
  providedIn: 'root'
})
export class Auth0LockService {
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private permissionService = inject(PermissionService);
  private lock: any;

  constructor() {
    // Initialize Auth0 Lock with your configuration
    this.lock = new Auth0Lock(
      SdmGlobal.auth0ClientId(), // Your Auth0 Client ID
      'login.sdmweb.scanburapps.com', // Your Auth0 Domain
      {
        autoclose: true,
        auth: {
          redirectUrl: window.location.origin + '/Login',
          responseType: 'token id_token',
          params: {
            scope: 'openid email profile user_metadata'
          }
        },
        theme: {
          logo: '../../images/scanbur_logo.png', 
          primaryColor: '#064d74' // Optional: your brand color
        },
        languageDictionary: {
          title: 'SDM Web Login'
        }
      }
    );

    // Handle authentication events
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    // Handle successful authentication
    this.lock.on('authenticated', (authResult: any) => {
      
      // Store the token and user info
      if (authResult && authResult.accessToken && authResult.idToken) {
        localStorage.setItem('token', authResult.idToken);
        localStorage.setItem('id_token', authResult.idToken);
        
        // Calculate and store the actual expiration timestamp
        const expirationTime = new Date().getTime() + (authResult.expiresIn * 1000);
        localStorage.setItem('tokenExpiresAt', expirationTime.toString());
        
        // Get user profile
        this.lock.getUserInfo(authResult.accessToken, (error: any, profile: any) => {
          if (error) {
            console.error('Auth0 Lock: Error getting user profile:', error);
            return;
          }
          localStorage.setItem('userEmail', profile.email);
          localStorage.setItem('userProfile', JSON.stringify(profile));
          
          // Redirect to requested URL or default based on user type
          const requestedUrl = authResult.state || this.getDefaultRedirectUrl(profile);
          
          // Use Angular Router within NgZone to ensure proper change detection
          this.ngZone.run(() => {
            this.router.navigate([requestedUrl]);
          });
        });
      }
    });

    // Handle authentication errors
    this.lock.on('authorization_error', (error: any) => {
      console.error('Auth0 Lock: Authorization error:', error);
      alert('Authentication failed: ' + (error.error_description || error.error || 'Unknown error'));
    });

    // Handle when lock is closed without authentication
    this.lock.on('hide', () => {
    });
  }

  /**
   * Show login modal for regular users
   */
  public showLogin(requestedUrl: string = 'Main'): void {
    const connection = SdmGlobal.auth0Connection();
    
    this.lock.show({
      allowedConnections: [connection],
      allowSignUp: false,
      auth: {
        redirectUrl: window.location.origin + '/Login',
        responseType: 'token id_token',
        params: {
          state: requestedUrl,
          scope: 'openid email profile user_metadata'
        }
      }
    });
  }

  /**
   * Show login modal for global admin users
   */
  public showGlobalLogin(requestedUrl: string = 'GlobalAdmin'): void {
    this.lock.show({
      allowedConnections: ['sdmglobal'],
      allowSignUp: false,
      auth: {
        redirectUrl: window.location.origin + '/LoginGlobal',
        responseType: 'token id_token',
        params: {
          state: requestedUrl,
          scope: 'openid email profile user_metadata'
        }
      }
    });
  }

  /**
   * Logout user (local logout only, like the old AngularJS code)
   */
  public logout(): void {
    // Determine if this is a global user BEFORE clearing localStorage
    const isGlobal = this.permissionService.isGlobalUser();
    
    // Clear local storage (same as old AngularJS code)
    localStorage.removeItem('token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('tokenExpiresAt');
	localStorage.removeItem('tenant');
    
    // Redirect within the app (like the old code did)
    const redirectUrl = isGlobal ? '/LoginGlobal' : '/Login';
    this.ngZone.run(() => {
      this.router.navigate([redirectUrl]);
    });
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
    
    if (!token || !tokenExpiresAt) {
      console.log('Auth0 Lock: No token or expiration time found');
      return false;
    }

    // Check if token is expired
    const expirationTime = parseInt(tokenExpiresAt);
    const currentTime = new Date().getTime();
    
    const isValid = currentTime < expirationTime;
    return isValid;
  }

  /**
   * Get current user profile
   */
  public getCurrentUser(): any {
    const userProfile = localStorage.getItem('userProfile');
    return userProfile ? JSON.parse(userProfile) : null;
  }

  private getDefaultRedirectUrl(profile: any): string {
    // Determine redirect based on PermissionService logic
    const isGlobal = this.permissionService.isGlobalUser();
    return isGlobal ? 'GlobalAdmin' : 'Main';
  }
}
